import React, { useMemo } from 'react';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import breakpoints from '@/constants/breakpoints.ts';
import Card from '@/components/ui/Card/Card.tsx';
import Tooltip from '@/components/ui/Tooltip/Tooltip.tsx';
import styles from './ComplianceSummary.module.scss';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import Select from '@/components/ui/Select/Select.tsx';
import Skeleton from 'react-loading-skeleton';
import { CountUp } from 'use-count-up';
import classNames from 'classnames';
import Co2Period = App.Enums.Co2Period;
import { trackEvent } from '@/utils/productAnalyticsUtil.ts';
import DataComplianceStatsResponse = App.Data.Response.DataComplianceStatsResponse;
import { getEmissionTargets } from '@/api/climatePro.ts';
import queryKeys from '@/constants/queryKeys.ts';
import { useModalStore } from '@/store/useModalStore.ts';
import Modal from '@/components/ui/Modal/Modal.tsx';
import { Link } from 'react-router-dom';
import routes from '@/constants/routes.ts';
import ArrowRightIcon from '@/assets/Icons/Right-Arrow-Extended.svg?react';
import InfoIcon from '@/assets/Icons/Info-Line.svg?react';
import CalendarIcon from '@/assets/Icons/Calendar-Schedule.svg?react';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';
import { actionNeededReportingPeriod, getDataCompliancePeriodOptions } from '@/constants/dataCompliance.ts';
import { getDataComplianceSummary } from '@/api/dataCompliance.ts';

interface ComplianceSummaryProps {
	timePeriod: Co2Period;
	setTimePeriod: (timePeriod: Co2Period) => void;
	dataComplianceQuery: UseQueryResult<DataComplianceStatsResponse>;
}

type CompletenessRateStatus = 'danger' | 'warning' | 'success' | undefined;

const reportingPeriodModal = 'reportingPeriodModal';
const getCompletenessRateStatus = (
	confirmedTripsCount?: number | null,
	complianceRate?: number | null,
): CompletenessRateStatus => {
	if ((confirmedTripsCount ?? 0) < 10 || complianceRate === null || complianceRate === undefined) {
		return undefined;
	}

	if (complianceRate < 40) return 'danger';
	if (complianceRate < 95) return 'warning';
	return 'success';
};

const ComplianceSummary: React.FC<ComplianceSummaryProps> = ({ timePeriod, setTimePeriod, dataComplianceQuery }) => {
	const { openModal, closeModal } = useModalStore();
	const selectedPeriodHasNoCompletedTrips = dataComplianceQuery.data?.confirmedTripsCount === 0;
	const emissionTargetsQuery = useQuery({
		queryKey: [queryKeys.getEmissionTargets],
		queryFn: getEmissionTargets,
	});
	const reportingPeriodDataComplianceQuery = useQuery({
		queryKey: [queryKeys.dataComplianceSummary, actionNeededReportingPeriod],
		queryFn: () => getDataComplianceSummary({ period: actionNeededReportingPeriod }),
		enabled:
			timePeriod !== actionNeededReportingPeriod &&
			dataComplianceQuery.isSuccess &&
			selectedPeriodHasNoCompletedTrips,
	});
	const completenessRateStatus = getCompletenessRateStatus(
		dataComplianceQuery.data?.confirmedTripsCount,
		dataComplianceQuery.data?.complianceRate,
	);
	const reportingPeriodCompanyTripsNeedingDetailsCount =
		reportingPeriodDataComplianceQuery.data?.nonCompliantTripsCount ?? 0;
	const shouldShowReportingPeriodNotice =
		timePeriod !== actionNeededReportingPeriod &&
		dataComplianceQuery.isSuccess &&
		selectedPeriodHasNoCompletedTrips &&
		reportingPeriodDataComplianceQuery.isSuccess &&
		reportingPeriodCompanyTripsNeedingDetailsCount > 0;
	const completenessRateStatusClass = completenessRateStatus
		? {
				danger: styles.completenessRateDanger,
				warning: styles.completenessRateWarning,
				success: styles.completenessRateSuccess,
			}[completenessRateStatus]
		: undefined;
	const currentEmissionTarget = useMemo(
		() => emissionTargetsQuery.data?.find(item => item.current),
		[emissionTargetsQuery.data],
	);
	const dataCompliancePeriodOptions = useMemo(
		() => getDataCompliancePeriodOptions(currentEmissionTarget),
		[currentEmissionTarget],
	);
	return (
		<Card className={styles.complianceCard}>
			<FlexBlock
				justifyContent="space-between"
				flexDirection={{ [breakpoints.zero]: 'column', [breakpoints.phoneWide]: 'row' }}
				rowGap="14px"
				className={styles.cardHeader}
			>
				<FlexBlock columnGap="4px" className={styles.cardTitle}>
					<h3>Trip data coverage</h3>
					<button
						type="button"
						aria-label="How trip data coverage works"
						className={styles.reportingPeriodInfoButton}
						onClick={() => {
							trackEvent(productAnalyticsEvents.climatePro.dataGap.tripDataCoverage.interact, {
								deduplicationKey: 'header.learn-how-this-works',
								postHogEventName: 'data_gap_header_info_clicked',
								postHogProperties: { category: 'data-gap' },
							});
							openModal(reportingPeriodModal);
						}}
					>
						<InfoIcon aria-hidden="true" />
						<span className={styles.reportingPeriodInfoTooltip} role="tooltip">
							Learn how this works
						</span>
					</button>
				</FlexBlock>
				<FlexBlock className={styles.periodControls}>
					<div className={styles.periodControlContent}>
						<Select
							required
							ariaLabel="Filter trip data coverage by period"
							value={timePeriod}
							onChange={value => {
								if (value) {
									setTimePeriod(value as Co2Period);
									trackEvent(productAnalyticsEvents.climatePro.dataGap.tripDataCoverage.interact, {
										deduplicationKey: `period.${value}`,
										postHogEventName: 'data_gap_period_changed',
										postHogProperties: {
											category: 'data-gap',
											period: value,
										},
									});
								}
							}}
							placeholder="Select period"
							name="period"
							options={dataCompliancePeriodOptions}
							className={styles.selectDropdown}
						/>
						{shouldShowReportingPeriodNotice && (
							<div className={styles.reportingPeriodNoticePositioner}>
								<div className={styles.reportingPeriodNotice}>
									<p>
										There {reportingPeriodCompanyTripsNeedingDetailsCount === 1 ? 'is' : 'are'}{' '}
										{reportingPeriodCompanyTripsNeedingDetailsCount}{' '}
										{reportingPeriodCompanyTripsNeedingDetailsCount === 1 ? 'trip' : 'trips'} within the annual
										reporting period.
									</p>
									<button
										type="button"
										aria-label="View trips in your company’s annual reporting period"
										onClick={() => {
											setTimePeriod(actionNeededReportingPeriod);
											trackEvent(productAnalyticsEvents.climatePro.dataGap.tripDataCoverage.interact, {
												deduplicationKey: 'empty-period.view-trips',
												postHogEventName: 'data_gap_empty_period_view_trips_clicked',
												postHogProperties: { category: 'data-gap' },
											});
										}}
									>
										View trips
									</button>
								</div>
							</div>
						)}
					</div>
				</FlexBlock>
			</FlexBlock>
			<div className={styles.statsCards}>
				<FlexBlock
					flexDirection="column"
					justifyContent="space-between"
					rowGap="16px"
					className={styles.statsCard}
				>
					<FlexBlock justifyContent="space-between" columnGap="6px">
						<FlexBlock alignItems="center" columnGap="2px">
							<p className={styles.header}>Completed trips</p>
							<span className={styles.statsCardInfo}>
								<Tooltip text="Includes completed trips with a confirmed booking. Each traveler on a shared itinerary is counted as a separate trip." />
							</span>
						</FlexBlock>
					</FlexBlock>
					{dataComplianceQuery.isLoading ? (
						<Skeleton height={60} />
					) : dataComplianceQuery.isError ? (
						<p>Data unavailable</p>
					) : (
						<FlexBlock flexDirection="column" justifyContent="space-between" rowGap="16px">
							<FlexBlock flexDirection="column" rowGap="6px">
								<p className={styles.num}>
									<CountUp
										end={dataComplianceQuery.data?.confirmedTripsCount || 0}
										isCounting
										duration={0.75}
									/>
								</p>
								<p className={styles.desc}>Trips taken in this period</p>
							</FlexBlock>
						</FlexBlock>
					)}
				</FlexBlock>
				<FlexBlock
					flexDirection="column"
					justifyContent="space-between"
					rowGap="16px"
					className={styles.statsCard}
				>
					<FlexBlock justifyContent="space-between" columnGap="6px">
						<FlexBlock alignItems="center" columnGap="2px">
							<p className={styles.header}>Trips needing details</p>
							<span className={styles.statsCardInfo}>
								<Tooltip text="Completed trips missing accommodation, transport, or confirmation that those details are not needed." />
							</span>
						</FlexBlock>
					</FlexBlock>
					{dataComplianceQuery.isLoading ? (
						<Skeleton height={60} />
					) : dataComplianceQuery.isError ? (
						<p>Data unavailable</p>
					) : (
						<FlexBlock flexDirection="column" justifyContent="space-between" rowGap="16px">
							<FlexBlock flexDirection="column" rowGap="6px">
								<p className={styles.num}>
									<CountUp
										end={dataComplianceQuery.data?.nonCompliantTripsCount || 0}
										isCounting
										duration={0.75}
									/>
								</p>
								<p className={styles.desc}>Trips missing data on transport or accommodation</p>
							</FlexBlock>
						</FlexBlock>
					)}
				</FlexBlock>
				<FlexBlock
					flexDirection="column"
					justifyContent="space-between"
					rowGap="16px"
					className={classNames(styles.statsCard, styles.completenessRateCard, completenessRateStatusClass)}
				>
					<FlexBlock justifyContent="space-between" columnGap="6px">
						<FlexBlock alignItems="center" columnGap="2px">
							<p className={styles.header}>Trip data coverage</p>
							<span className={styles.statsCardInfo}>
								<Tooltip text="The share of completed trips that have all required reporting details provided." />
							</span>
						</FlexBlock>
					</FlexBlock>
					{dataComplianceQuery.isLoading ? (
						<Skeleton height={60} />
					) : dataComplianceQuery.isError ? (
						<p>Data unavailable</p>
					) : (
						<FlexBlock flexDirection="column" justifyContent="space-between" rowGap="16px">
							<FlexBlock flexDirection="column" rowGap="6px">
								<p className={styles.num}>
									{selectedPeriodHasNoCompletedTrips ? (
										<span aria-label="Not applicable">-</span>
									) : (
										<>
											<CountUp
												end={dataComplianceQuery.data?.complianceRate || 0}
												isCounting
												duration={0.75}
											/>
											%
										</>
									)}
								</p>
								<p className={styles.desc}>Share of trips with full data</p>
							</FlexBlock>
						</FlexBlock>
					)}
				</FlexBlock>
			</div>
			<Modal name={reportingPeriodModal} className={styles.reportingPeriodModal}>
				<h2 className={styles.modalTitle}>
					<span className={styles.modalTitleIcon} aria-hidden="true">
						<CalendarIcon />
					</span>
					How trip data coverage works
				</h2>
				<p>
					Trip data coverage shows how many completed trips have the accommodation and transport details
					needed for CO₂ reporting. Use this card to understand how complete your reporting data is for the
					selected period.
				</p>
				<p>
					The reporting period defines the date range used for your CO₂ reporting and targets. This page uses
					it to show trip data coverage for completed trips inside that period.
				</p>
				<p>
					Company administrators can view and edit the reporting period in CO₂ budget settings, where the
					current emission target is configured.
				</p>
				<Link to={routes.climateProSetting} onClick={() => closeModal()}>
					<ArrowRightIcon aria-hidden="true" />
					Edit reporting period
				</Link>
			</Modal>
		</Card>
	);
};

export default ComplianceSummary;
